(() => {
  'use strict';
  const settings = window.IcemanSettings || {};
  const root = document.documentElement;
  if (settings.snowflakePattern) root.style.setProperty('--snowflake-pattern', `url("${settings.snowflakePattern}")`);

  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const storage = {
    get(k, fallback = null) { try { return localStorage.getItem(k) ?? fallback; } catch { return fallback; } },
    set(k, v) { try { localStorage.setItem(k, v); } catch {} }
  };

  let audio = null;
  let audioCtx = null;
  let soundPreference = storage.get('icemanSound', 'unset');
  const muteBtn = document.getElementById('IcemanMuteToggle');

  function ensureAudio() {
    if (audio || !settings.musicUrl) return audio;
    audio = new Audio(settings.musicUrl);
    audio.loop = true;
    audio.preload = 'none';
    audio.volume = 0;
    return audio;
  }
  function fadeAudio(target, duration = 1200) {
    if (!audio) return;
    const start = audio.volume;
    const startTime = performance.now();
    function tick(now) {
      const p = Math.min(1, (now - startTime) / duration);
      audio.volume = start + (target - start) * p;
      if (p < 1) requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
  }
  async function startMusic() {
    const a = ensureAudio();
    if (!a) return;
    try {
      await a.play();
      fadeAudio(0.72, 1500);
      if (muteBtn) { muteBtn.hidden = false; muteBtn.textContent = '🔊'; muteBtn.setAttribute('aria-label', 'Mute sound'); }
    } catch (e) {
      if (muteBtn) { muteBtn.hidden = false; muteBtn.textContent = '🔇'; muteBtn.setAttribute('aria-label', 'Play sound'); }
    }
  }
  function stopMusic() {
    if (!audio) return;
    fadeAudio(0, 300);
    setTimeout(() => { if (audio && audio.volume < 0.05) audio.pause(); }, 340);
    if (muteBtn) { muteBtn.textContent = '🔇'; muteBtn.setAttribute('aria-label', 'Unmute sound'); }
  }
  function canPlayEffects() { return storage.get('icemanSound') === 'sound'; }
  function playEffect(url, volume = 0.45) {
    if (!url || !canPlayEffects()) return false;
    try {
      const fx = new Audio(url);
      fx.volume = volume;
      fx.play().catch(() => {});
      return true;
    } catch { return false; }
  }
  function getAudioCtx() {
    if (!canPlayEffects()) return null;
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;
    if (!audioCtx) audioCtx = new Ctx();
    return audioCtx;
  }
  function crackSound(strength = 1) {
    if (playEffect(settings.crackUrl, Math.min(0.7, 0.22 + strength * 0.12))) return;
    const ctx = getAudioCtx(); if (!ctx) return;
    const bufferSize = ctx.sampleRate * 0.18;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / bufferSize, 2.2);
    const noise = ctx.createBufferSource(); noise.buffer = buffer;
    const filter = ctx.createBiquadFilter(); filter.type = 'highpass'; filter.frequency.value = 900 + strength * 250;
    const gain = ctx.createGain(); gain.gain.value = 0.12 * strength;
    noise.connect(filter); filter.connect(gain); gain.connect(ctx.destination); noise.start();
  }
  function shatterSound() {
    if (playEffect(settings.shatterUrl, 0.72)) return;
    crackSound(3.2);
    const ctx = getAudioCtx(); if (!ctx) return;
    for (let i = 0; i < 8; i++) {
      const osc = ctx.createOscillator(); const gain = ctx.createGain();
      osc.type = 'sine'; osc.frequency.value = 420 + Math.random() * 900;
      gain.gain.setValueAtTime(0.025, ctx.currentTime + i * 0.018);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.28 + i * 0.018);
      osc.connect(gain); gain.connect(ctx.destination);
      osc.start(ctx.currentTime + i * 0.018); osc.stop(ctx.currentTime + 0.32 + i * 0.018);
    }
  }

  if (muteBtn) {
    muteBtn.addEventListener('click', () => {
      const playing = audio && !audio.paused && audio.volume > 0.05;
      if (playing) {
        storage.set('icemanSound', 'silent');
        stopMusic();
      } else {
        storage.set('icemanSound', 'sound');
        soundPreference = 'sound';
        if (muteBtn) { muteBtn.hidden = false; muteBtn.textContent = '🔊'; muteBtn.setAttribute('aria-label', 'Sound selected'); }
        if (musicAllowedNow()) startMusic();
      }
    });
  }

  // Cinematic entry / freeze / break-through journey
  const intro = document.getElementById('IcemanIntro');
  const freeze = document.getElementById('IcemanFreeze');
  const iceBreak = document.getElementById('IcemanIceBreak');
  const introDone = storage.get('icemanIntroComplete') === 'true';
  const shouldShowIntro = settings.introMode !== 'off' && (settings.introMode === 'always' || !introDone);
  function musicAllowedNow() {
    return storage.get('icemanIntroComplete') === 'true' || !shouldShowIntro;
  }

  function hideIntroInstantly() {
    if (intro) { intro.hidden = true; intro.classList.add('is-hidden'); }
    document.body.classList.remove('iceman-locked');
    if (soundPreference === 'sound') { if (muteBtn) muteBtn.hidden = false; startMusic(); }
    else if (soundPreference === 'silent' && muteBtn) { muteBtn.hidden = false; muteBtn.textContent = '🔇'; }
  }
  function runIceBreak() {
    if (!iceBreak) return revealStore();
    iceBreak.hidden = false;
    iceBreak.dataset.taps = '0';
    let taps = 0;
    const onTap = (ev) => {
      ev.preventDefault();
      taps = Math.min(3, taps + 1);
      iceBreak.dataset.taps = String(taps);
      if (taps < 3) crackSound(taps);
      if (taps === 3) {
        shatterSound();
        createShards(iceBreak, 18);
        iceBreak.classList.add('is-shattering');
        iceBreak.removeEventListener('pointerdown', onTap);
        setTimeout(revealStore, 920);
      }
    };
    iceBreak.addEventListener('pointerdown', onTap);
  }
  function revealStore() {
    if (iceBreak) iceBreak.hidden = true;
    if (freeze) freeze.classList.remove('is-active');
    if (intro) { intro.hidden = true; intro.classList.add('is-hidden'); }
    document.body.classList.remove('iceman-locked');
    storage.set('icemanIntroComplete', 'true');
    if (storage.get('icemanSound') === 'sound') startMusic();
  }
  function beginJourney(withSound) {
    storage.set('icemanSound', withSound ? 'sound' : 'silent');
    soundPreference = withSound ? 'sound' : 'silent';
    if (muteBtn) { muteBtn.hidden = false; muteBtn.textContent = withSound ? '🔊' : '🔇'; }
    // Music intentionally waits until after the third ice crack/shatter so it does not overlap the crack sounds.
    if (audioCtx && audioCtx.state === 'suspended') audioCtx.resume();
    if (prefersReduced) return revealStore();
    intro.classList.add('is-transitioning');
    setTimeout(() => freeze && freeze.classList.add('is-active'), 550);
    setTimeout(() => { intro.classList.add('is-hidden'); runIceBreak(); }, 2350);
  }
  if (intro) {
    if (!shouldShowIntro) hideIntroInstantly();
    else {
      document.body.classList.add('iceman-locked');
      intro.querySelectorAll('[data-iceman-enter]').forEach(btn => {
        btn.addEventListener('click', () => beginJourney(btn.dataset.icemanEnter === 'sound'));
      });
    }
  }

  function createShards(container, count) {
    for (let i = 0; i < count; i++) {
      const shard = document.createElement('i');
      shard.className = 'ice-shard';
      const angle = (Math.PI * 2 * i) / count + Math.random() * 0.5;
      const dist = 45 + Math.random() * 55;
      shard.style.setProperty('--tx', `${Math.cos(angle) * dist}vw`);
      shard.style.setProperty('--ty', `${Math.sin(angle) * dist}vh`);
      shard.style.setProperty('--rot', `${Math.random() * 720 - 360}deg`);
      shard.style.left = `${45 + Math.random() * 10}%`;
      shard.style.top = `${45 + Math.random() * 10}%`;
      shard.style.animationDelay = `${Math.random() * 0.08}s`;
      container.appendChild(shard);
      setTimeout(() => shard.remove(), 1200);
    }
  }

  // Canvas snowfall: three depth layers, variable size, rotation, drift and speed.
  const canvas = document.getElementById('IcemanSnowCanvas');
  const ctx = canvas && canvas.getContext('2d');
  let flakes = [], w = 0, h = 0, dpr = 1, snowRunning = true, last = performance.now();
  function resizeSnow() {
    if (!canvas || !ctx) return;
    dpr = Math.min(2, window.devicePixelRatio || 1);
    w = window.innerWidth; h = window.innerHeight;
    canvas.width = Math.floor(w * dpr); canvas.height = Math.floor(h * dpr);
    canvas.style.width = w + 'px'; canvas.style.height = h + 'px';
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const base = prefersReduced ? 45 : Math.min(210, Math.floor((w * h) / 7800));
    flakes = Array.from({ length: base }, () => makeFlake(true));
  }
  function makeFlake(randomY = false) {
    const layerPick = Math.random();
    const layer = layerPick < .48 ? 0 : layerPick < .84 ? 1 : 2;
    return {
      x: Math.random() * w, y: randomY ? Math.random() * h : -20,
      r: (layer + 1) * (0.7 + Math.random() * 1.65),
      speed: (layer + 1) * (0.18 + Math.random() * 0.45),
      drift: (Math.random() - 0.5) * (0.35 + layer * 0.16),
      rot: Math.random() * Math.PI * 2, spin: (Math.random() - 0.5) * 0.025,
      alpha: 0.32 + layer * 0.18 + Math.random() * 0.28, layer
    };
  }
  function drawFlake(f) {
    ctx.save(); ctx.translate(f.x, f.y); ctx.rotate(f.rot); ctx.globalAlpha = f.alpha;
    ctx.strokeStyle = '#e9fbff'; ctx.lineWidth = Math.max(0.8, f.r * 0.18); ctx.lineCap = 'round';
    for (let i = 0; i < 6; i++) { ctx.rotate(Math.PI / 3); ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(0, f.r * 2.4); ctx.stroke(); }
    ctx.beginPath(); ctx.arc(0, 0, f.r * .38, 0, Math.PI * 2); ctx.fillStyle = '#ffffff'; ctx.fill(); ctx.restore();
  }
  function snowLoop(now) {
    if (!ctx) return;
    const dt = Math.min(34, now - last); last = now;
    ctx.clearRect(0, 0, w, h);
    if (snowRunning) {
      flakes.forEach(f => {
        f.y += f.speed * dt * 0.09; f.x += (f.drift + Math.sin((f.y + f.rot) * 0.012) * 0.18) * dt * 0.1; f.rot += f.spin * dt * 0.08;
        if (f.y > h + 28 || f.x < -40 || f.x > w + 40) Object.assign(f, makeFlake(false), { y: -25, x: Math.random() * w });
        drawFlake(f);
      });
    }
    requestAnimationFrame(snowLoop);
  }
  if (canvas && ctx) { resizeSnow(); requestAnimationFrame(snowLoop); window.addEventListener('resize', resizeSnow, { passive: true }); }
  document.addEventListener('visibilitychange', () => {
    snowRunning = !document.hidden;
    if (document.hidden && audio) audio.pause();
    else if (storage.get('icemanSound') === 'sound' && musicAllowedNow()) startMusic();
  });

  // Frost cursor trail, UI hover shimmer, snow puff click effects.
  let lastTrail = 0;
  document.addEventListener('pointermove', (e) => {
    if (prefersReduced || e.pointerType === 'touch') return;
    const now = performance.now(); if (now - lastTrail < 28) return; lastTrail = now;
    const dot = document.createElement('i'); dot.className = 'frost-dot'; dot.style.left = `${e.clientX}px`; dot.style.top = `${e.clientY}px`; document.body.appendChild(dot); setTimeout(() => dot.remove(), 800);
    const card = e.target.closest && e.target.closest('[data-frost-card]');
    if (card) { const rect = card.getBoundingClientRect(); card.style.setProperty('--mx', `${e.clientX - rect.left}px`); card.style.setProperty('--my', `${e.clientY - rect.top}px`); }
  }, { passive: true });

  document.addEventListener('click', (e) => {
    const isButton = e.target.closest && e.target.closest('button,.iceman-btn,a[href]');
    if (!isButton || prefersReduced) return;
    for (let i = 0; i < 9; i++) {
      const puff = document.createElement('i'); puff.className = 'snow-puff';
      puff.style.left = `${e.clientX}px`; puff.style.top = `${e.clientY}px`;
      puff.style.setProperty('--x', `${(Math.random() - .5) * 80}px`); puff.style.setProperty('--y', `${(Math.random() - .8) * 80}px`);
      document.body.appendChild(puff); setTimeout(() => puff.remove(), 700);
    }
  });

  // Subtle parallax on scenic sections.
  document.addEventListener('pointermove', (e) => {
    if (prefersReduced) return;
    const px = (e.clientX / window.innerWidth - .5) * 10;
    const py = (e.clientY / window.innerHeight - .5) * 8;
    document.querySelectorAll('[data-parallax]').forEach(el => { el.style.transform = `translate3d(${px * .18}px,${py * .18}px,0)`; });
  }, { passive: true });


  function sanitizeLimitedEditionCopy() {
    const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    nodes.forEach(node => {
      node.nodeValue = node.nodeValue
        .replace(/limited\s+to\s+\d+(?:\s*[\/–-]\s*\d+)?/gi, 'Limited Edition')
        .replace(/limited\s+edition\s+of\s+\d+(?:\s*[\/–-]\s*\d+)?/gi, 'Limited Edition')
        .replace(/buy\s*now/gi, 'Sold Out')
        .replace(/add\s*to\s*cart/gi, 'Sold Out')
        .replace(/checkout/gi, 'Sold Out');
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', sanitizeLimitedEditionCopy); else sanitizeLimitedEditionCopy();

})();
